import { ArrowRightArrowLeft } from '@gravity-ui/icons';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import Divider from '@/components/Divider';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useIdentifierSignInMethods from '@/pages/IdentifierSignIn/use-identifier-sign-in-methods';
import { type VerificationCodeIdentifier } from '@/types';

import MethodSwitchButton from './MethodSwitchButton';
import PasskeySignInLink from './PasskeySignInLink';
import PasswordSignInLink from './PasswordSignInLink';
import VerificationCodeLink from './VerificationCodeLink';

type Props = {
  readonly className?: string;
  readonly hasPassword?: boolean;
  readonly hasVerificationCode?: boolean;
  readonly identifier?: VerificationCodeIdentifier;
  readonly value?: string;
};

/**
 * Link component shown on the password page and verification code page
 * when passkey sign-in is enabled and the user may have passkey credentials.
 *
 * Navigates to the verification methods selection page that shows all available
 * options (passkey, password, verification code).
 *
 * The verification methods page reads identifier from UserInteractionContext
 * and available methods from useSieMethods(), so no extra state is needed.
 */
const SwitchToVerificationMethodsLink = ({
  className,
  hasPassword,
  hasVerificationCode,
  identifier,
  value,
}: Props) => {
  const { pathname } = useLocation();
  const navigate = useNavigateWithPreservedSearchParams();
  const { identifierHasBoundPasskey, isPasskeySignInEnabled } = useIdentifierSignInMethods();

  const optionCounts = [
    isPasskeySignInEnabled && identifierHasBoundPasskey,
    hasPassword,
    hasVerificationCode,
  ].filter(Boolean).length;

  const option = (() => {
    if (optionCounts > 2) {
      return (
        <MethodSwitchButton
          icon={<ArrowRightArrowLeft />}
          text="mfa.try_another_verification_method"
          onClick={() => {
            navigate('/sign-in/verification-methods');
          }}
        />
      );
    }

    if (
      isPasskeySignInEnabled &&
      identifierHasBoundPasskey &&
      !pathname.endsWith('/sign-in/passkey') &&
      identifier &&
      value
    ) {
      return <PasskeySignInLink identifier={identifier} value={value} />;
    }
    if (hasPassword && !pathname.endsWith('/sign-in/password')) {
      return <PasswordSignInLink />;
    }
    if (hasVerificationCode && !pathname.endsWith('/sign-in/passcode') && identifier && value) {
      return <VerificationCodeLink identifier={identifier} value={value} />;
    }
    return null;
  })();

  if (!option) {
    return null;
  }

  return (
    <div className={classNames('flex flex-col gap-6', className)}>
      <Divider label="description.or" />
      {option}
    </div>
  );
};

export default SwitchToVerificationMethodsLink;
